import { useState, useRef, useEffect } from "react";
import type { Token } from "@/types";

interface TokenAvatarProps {
	token: Token;
	size?: number;
}

export function TokenAvatar({ token, size = 28 }: TokenAvatarProps) {
	const [status, setStatus] = useState<"idle" | "loaded" | "error">("idle");

	const letterFallback = (
		<span
			style={{
				width: size,
				height: size,
				background: token.color,
				fontSize: Math.round(size * 0.42),
				borderRadius: "50%",
				display: "inline-grid",
				placeItems: "center",
				color: "white",
				fontWeight: 700,
				flexShrink: 0,
				userSelect: "none",
			}}
		>
			{token.symbol[0]}
		</span>
	);

	if (!token.logoURI || status === "error") return letterFallback;

	return (
		<span style={{ position: "relative", width: size, height: size, flexShrink: 0, display: "inline-block" }}>
			{/* Gradient placeholder shown while image hasn't loaded yet */}
			{status !== "loaded" && (
				<span
					aria-hidden
					style={{
						position: "absolute",
						inset: 0,
						borderRadius: "50%",
						background: token.color,
						display: "grid",
						placeItems: "center",
						color: "white",
						fontWeight: 700,
						fontSize: Math.round(size * 0.42),
					}}
				>
					{token.symbol[0]}
				</span>
			)}
			<img
				src={token.logoURI}
				alt={token.symbol}
				width={size}
				height={size}
				loading="lazy"
				decoding="async"
				onLoad={() => setStatus("loaded")}
				onError={() => setStatus("error")}
				style={{
					borderRadius: "50%",
					objectFit: "cover",
					display: "block",
					opacity: status === "loaded" ? 1 : 0,
					transition: "opacity 150ms ease",
					position: "absolute",
					inset: 0,
				}}
			/>
		</span>
	);
}

/* ── Lazy-visible wrapper ─────────────────────────────────────────────────────
   Renders children only once the element has scrolled into the viewport.
   Uses IntersectionObserver with a root margin so rows pre-load slightly before
   they appear (better perceived performance while scrolling quickly).          */

interface LazyVisibleProps {
	children: React.ReactNode;
	height: number;
}

export function LazyVisible({ children, height }: LazyVisibleProps) {
	const ref = useRef<HTMLDivElement>(null);
	const [visible, setVisible] = useState(false);

	useEffect(() => {
		const el = ref.current;
		if (!el) return;

		const observer = new IntersectionObserver(
			([entry]) => {
				if (entry.isIntersecting) {
					setVisible(true);
					observer.disconnect();
				}
			},
			{ rootMargin: "120px" },
		);

		observer.observe(el);
		return () => observer.disconnect();
	}, []);

	if (visible) return <>{children}</>;

	return (
		<div
			ref={ref}
			style={{ height, width: "100%", borderRadius: 10, background: "var(--surface-2)" }}
		/>
	);
}
